import React, { useState, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { useLMS } from '../../context/LMSContext';
import { db } from '../../lib/supabase';

/**
 * EligibilityTracker - Computes money-back guarantee eligibility in real-time.
 *
 * Rules:
 *   ATTENDANCE: "Present" & "Excused" = eligible. "Late", "Absent", "Left Early" = ineligible. "Other" excluded.
 *   ASSIGNMENTS: Only assignments with a due date count. Must be submitted on time (not late). Late or missing = ineligible.
 *   Computed in real-time from attendance_records + assignment_submissions.
 */
const EligibilityTracker = ({ cohortId }) => {
  const {
    modules,
    getUsersForCohort,
    getCohortById,
    cohortItemOverrides
  } = useLMS();

  const [attendanceData, setAttendanceData] = useState({ sessions: [], records: [] });
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);

  const students = getUsersForCohort(cohortId).filter(s => s.role === 'student');
  const cohort = getCohortById(cohortId);

  useEffect(() => {
    if (cohortId) loadEligibilityData();
  }, [cohortId]);

  const loadEligibilityData = async () => {
    setLoading(true);
    try {
      const [attRes, subRes] = await Promise.all([
        db.getCohortAttendanceRecords(cohortId),
        db.getCohortSubmissionsForEligibility(cohortId)
      ]);
      if (attRes.data) setAttendanceData(attRes.data);
      if (subRes.data) setSubmissions(subRes.data);
    } catch (err) {
      console.error('Error loading eligibility data:', err);
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  // Build a map of assignment items that have due dates
  const assignmentsWithDueDates = useMemo(() => {
    if (!cohort) return [];
    const overrides = cohortItemOverrides[cohortId] || {};
    const startDateStr = cohort.startDate || cohort.start_date;
    if (!startDateStr) return [];

    const [sY, sM, sD] = startDateStr.split('-').map(Number);
    const cohortStart = new Date(sY, sM - 1, sD);
    const dayMap = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };

    const calculateDueDate = (weekNumber, dayOfWeek) => {
      const targetDayIndex = dayMap[dayOfWeek] ?? null;
      if (targetDayIndex === null) return null;
      const cohortStartDayIndex = cohortStart.getDay();
      let daysToTargetDay = (targetDayIndex - cohortStartDayIndex + 7) % 7;
      const weekOffset = ((weekNumber || 1) - 1) * 7;
      const eventDate = new Date(cohortStart);
      eventDate.setDate(eventDate.getDate() + weekOffset + daysToTargetDay);
      // Set to end of day for due date comparison
      eventDate.setHours(23, 59, 59, 999);
      return eventDate;
    };

    const items = [];
    modules.forEach(module => {
      const weekNum = module.weekNumber || module.week_number;
      (module.items || []).forEach(item => {
        if (!['assignment', 'quiz', 'exam', 'workbook'].includes(item.type)) return;
        if (!item.countsForEligibility) return;

        const override = overrides[item.id];
        // Check for explicit due_date override first
        const explicitDueDate = override?.due_date || override?.dueDate || null;
        if (explicitDueDate) {
          items.push({
            id: item.id,
            title: item.title,
            weekNumber: weekNum,
            dueDate: new Date(explicitDueDate)
          });
          return;
        }

        // Check for due_day (override or master)
        const dueDay = override?.due_day || item.dueDay || item.due_day;
        if (dueDay) {
          const calculated = calculateDueDate(weekNum, dueDay);
          if (calculated) {
            items.push({
              id: item.id,
              title: item.title,
              weekNumber: weekNum,
              dueDate: calculated
            });
          }
        }
        // If no due_date and no due_day → excluded from eligibility
      });
    });
    return items;
  }, [modules, cohort, cohortItemOverrides, cohortId]);

  // Compute eligibility for each student
  const eligibilityMap = useMemo(() => {
    const map = {};
    const { sessions, records } = attendanceData;

    // Build record lookup: sessionId+studentId -> status
    const recordLookup = {};
    records.forEach(r => {
      recordLookup[`${r.session_id}-${r.student_id}`] = r.status;
    });

    // Build submission lookup: studentId+itemId -> submission
    const subLookup = {};
    submissions.forEach(s => {
      subLookup[`${s.student_id}-${s.module_item_id}`] = s;
    });

    students.forEach(student => {
      const reasons = [];
      let isEligible = true;

      // --- ATTENDANCE CHECK ---
      // Only evaluate sessions whose date has already occurred (today or earlier).
      // Future sessions should not count against eligibility.
      const today = new Date();
      today.setHours(23, 59, 59, 999);

      sessions.forEach(session => {
        // Skip sessions that haven't occurred yet
        if (session.session_date) {
          const sessionDate = new Date(session.session_date + 'T23:59:59');
          if (sessionDate > today) return;
        }

        const key = `${session.id}-${student.id}`;
        const status = recordLookup[key];

        // "Other" is excluded entirely
        if (status === 'other') return;

        // "Present" and "Excused" are eligible
        if (status === 'present' || status === 'excused') return;

        // "Late" attendance
        if (status === 'late') {
          isEligible = false;
          const dateStr = session.session_date
            ? new Date(session.session_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
            : 'unknown date';
          reasons.push({ type: 'attendance', text: `Late to class on ${dateStr}`, date: session.session_date });
          return;
        }

        // "Left Early"
        if (status === 'left-early') {
          isEligible = false;
          const dateStr = session.session_date
            ? new Date(session.session_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
            : 'unknown date';
          reasons.push({ type: 'attendance', text: `Left early on ${dateStr}`, date: session.session_date });
          return;
        }

        // No record or "Absent" — only for sessions that have already occurred
        isEligible = false;
        const dateStr = session.session_date
          ? new Date(session.session_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
          : 'unknown date';
        reasons.push({ type: 'attendance', text: `Absent on ${dateStr}`, date: session.session_date });
      });

      // --- ASSIGNMENT CHECK ---
      assignmentsWithDueDates.forEach(assignment => {
        // Skip assignments whose due date hasn't passed yet
        if (assignment.dueDate && assignment.dueDate > today) return;

        const subKey = `${student.id}-${assignment.id}`;
        const sub = subLookup[subKey];

        if (!sub || sub.status === 'draft') {
          // Not submitted (or only a draft)
          isEligible = false;
          reasons.push({ type: 'assignment', text: `Assignment '${assignment.title}' not submitted` });
          return;
        }

        // Check if submitted late
        if (sub.is_late) {
          isEligible = false;
          reasons.push({ type: 'assignment', text: `Assignment '${assignment.title}' submitted late` });
          return;
        }

        // Double-check: compare submitted_at with due date
        if (sub.submitted_at && assignment.dueDate) {
          const submittedAt = new Date(sub.submitted_at);
          if (submittedAt > assignment.dueDate) {
            isEligible = false;
            reasons.push({ type: 'assignment', text: `Assignment '${assignment.title}' submitted late` });
          }
        }
      });

      map[student.id] = { isEligible, reasons };
    });

    return map;
  }, [students, attendanceData, submissions, assignmentsWithDueDates]);

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
        <div className="spinner" style={{ margin: '0 auto 12px' }}></div>
        <p>Loading eligibility data...</p>
      </div>
    );
  }

  if (!cohortId) return null;

  const eligibleCount = students.filter(s => eligibilityMap[s.id]?.isEligible).length;
  const ineligibleCount = students.length - eligibleCount;

  return (
    <Sentry.ErrorBoundary fallback={<div style={{ padding: '20px', color: '#dc2626' }}>Error loading eligibility tracker.</div>}>
      <div className="eligibility-tracker">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0 }}>Money-Back Guarantee Eligibility</h3>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '13px' }}>
              Computed in real-time from attendance and assignment data
            </p>
          </div>
          <button className="btn-secondary" onClick={loadEligibilityData} style={{ fontSize: '13px' }}>
            Refresh
          </button>
        </div>

        {/* Summary Stats */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            flex: 1, padding: '12px 16px', borderRadius: '8px',
            backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0'
          }}>
            <span style={{ fontSize: '24px', fontWeight: '700', color: '#065f46' }}>{eligibleCount}</span>
            <span style={{ display: 'block', fontSize: '12px', color: '#065f46', fontWeight: '500' }}>Eligible</span>
          </div>
          <div style={{
            flex: 1, padding: '12px 16px', borderRadius: '8px',
            backgroundColor: '#fef2f2', border: '1px solid #fecaca'
          }}>
            <span style={{ fontSize: '24px', fontWeight: '700', color: '#991b1b' }}>{ineligibleCount}</span>
            <span style={{ display: 'block', fontSize: '12px', color: '#991b1b', fontWeight: '500' }}>Ineligible</span>
          </div>
          <div style={{
            flex: 1, padding: '12px 16px', borderRadius: '8px',
            backgroundColor: '#f9fafb', border: '1px solid #e5e7eb'
          }}>
            <span style={{ fontSize: '24px', fontWeight: '700', color: '#374151' }}>{students.length}</span>
            <span style={{ display: 'block', fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Total Students</span>
          </div>
        </div>
      </div>
    </Sentry.ErrorBoundary>
  );
};

export default EligibilityTracker;
